import React from 'react';
import 'react-fastclick';
import thunkMiddleware from 'redux-thunk';
import { applyMiddleware, createStore, combineReducers } from 'redux';
import { Provider } from 'react-redux';
import { render } from 'react-dom';
import { IndexRoute, Redirect, Router, Route, browserHistory } from 'react-router';
import { syncHistoryWithStore, routerReducer, routerMiddleware, routerActions } from 'react-router-redux';
import $ from 'jquery';
import 'hudl-react-kickoff/legacy/lib/kickoff/layout-main';
import { data, model } from 'hudl-base';

import 'entries/video-page-video/utility/page-load';

import './team-profile-page.scss';
import 'react-select/dist/react-select.css';
import teamProfileApp from 'reducers/team-profile';
import localforage from 'common/localforage';
import * as OnboardingSteps from 'common/onboarding-steps';
import * as TeamSortTypes from 'common/team-sort-types';
import ProfileType from 'common/container-subtype';
import UsageLogger from 'common/usage-logger';
import { setupRateLimitedMouseFlow } from 'common/mouse-flow';
import App from 'components/team-profile/app';
import TeamTimelineTab from 'components/team-profile/timeline/team-timeline-tab';
import TeamHighlightsTab from 'components/team-profile/highlights/team-highlights-tab';
import TeamTab from 'components/team-profile/team-tab';
import ScheduleTab from 'components/team-profile/schedule/schedule-tab';
import GamesTab from 'components/shared/profile-tabs/games/games-tab';
import g11n from 'hudl-g11n';
import WebNav from 'components/shared/nav';
import * as HudlFeed from 'hudl-feed';
import loadAsyncScripts from 'common/async-scripts';
import promoteDynamicContent from 'utility/comscore';

$(() => {
  window.__hudlui_Generic && window.__hudlui_Generic.init();

  const usageLogger = new UsageLogger();
  const clientModel = model.get();
  const IntlProvider = g11n.ReactIntl.IntlProvider;
  const pageViewKey =
     `${clientModel.user.userId}-team-profile-page-views`;
  const feedbackDismissedKey =
    `${clientModel.user.userId}-team-profile-feedback-dismissed`;
  const feedbackCtaDismissedKey =
    `${clientModel.user.userId}-team-profile-cta-feedback-dismissed`;
  const i18n = {
    locale: data.get('i18nLocale'),
    messages: data.get('i18nMessages'),
  };

  // Prevent the browser from going to the last scroll position
  const isMobile = clientModel.isMobile;
  if (!isMobile && 'scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }

  const hasHighlightsDisabled = clientModel.hasHighlightsDisabled;

  const isRecruiter = clientModel.recruiterAccess &&
    (clientModel.recruiterAccess.hasRecruiterAccess || clientModel.recruiterAccess.canUpgradeForAccess);

  localforage.hudlGetMany(
  feedbackDismissedKey,
  pageViewKey,
  feedbackCtaDismissedKey).then(values => {
    let feedbackDismissed = values[0];
    const pageViews = values[1] || 0;
    const feedbackCtaDismissed = values[2];

    let teamSortType = TeamSortTypes.MOST_VIEWS;
    if (clientModel.user &&
      (clientModel.user.isRecruiter || (clientModel.user.isPrivilegedUser && !clientModel.forcePublicView))) {
      teamSortType = TeamSortTypes.JERSEY_NUMBER;
    }

    const onboardingSteps = [];
    if (clientModel.team.onboarding.tagline) {
      onboardingSteps.push(OnboardingSteps.TAGLINE);
    }
    if (clientModel.team.onboarding.logo) {
      onboardingSteps.push(OnboardingSteps.LOGO);
    }
    if (clientModel.team.onboarding.photo) {
      onboardingSteps.push(OnboardingSteps.BANNER);
    }
    const anyOnboarding = !!onboardingSteps.length;
    if (anyOnboarding) {
      onboardingSteps.push(OnboardingSteps.SHARE);
    }
    let isFeedback = false;
    if (clientModel.feedback.feedbackEnabled && !feedbackCtaDismissed) {
      switch (pageViews) {
      case 49:
        localforage.setItem(feedbackDismissedKey, false);
        feedbackDismissed = false;
        isFeedback = true;
        break;
      case 29:
        localforage.setItem(feedbackDismissedKey, false);
        feedbackDismissed = false;
        isFeedback = true;
        break;
      case 9:
        localforage.setItem(feedbackDismissedKey, false);
        isFeedback = true;
        break;
      default:
        break;
      }
      if (pageViews > 49 && !feedbackDismissed) {
        isFeedback = true;
      }
      if (pageViews > 29 && !feedbackDismissed) {
        isFeedback = true;
      }
      if (pageViews > 9 && !feedbackDismissed) {
        isFeedback = true;
      }
    }

    const defaultState = {
      bulkSaves: {
        isBulk: false,
      },
      customize: {
        color: {
          value: clientModel.team.primaryColor || null,
          saveSuccess: false,
          saveError: false,
          error: false,
        },
        profileImage: {
          value: clientModel.team.teamLogoUri,
        },
        bannerImage: {
          value: clientModel.team.teamCoverImage,
        },
        tagline: {
          value: clientModel.team.tagline,
          saveSuccess: false,
          saveError: false,
          error: false,
        },
      },
      feedback: {
        feedbackEnabled: isFeedback,
      },
      highlights: {
        sort: clientModel.user.isPrivilegedUser || isRecruiter ? 'recent' : 'views',
      },
      highlightEditorIdentityData: clientModel.highlightEditorIdentityData,
      onboarding: {
        isDismissed: true,
        steps: onboardingSteps,
        currentStep: onboardingSteps[0],
      },
      roster: {
        seasons: clientModel.roster.seasons || [],
        teamSortType: teamSortType,
      },
      schedule: {
        view: 'games',
      },
      recruiterAccess: clientModel.recruiterAccess,
      games: {
        profileType: ProfileType.Team,
        fullGames: null,
        loading: false,
        error: false,
        teams: clientModel.games.teams,
        selectedTeamId: clientModel.games.defaultTeamId,
        selectedSeasonId: clientModel.games.defaultTeamId ?
          clientModel.games.teams[clientModel.games.defaultTeamId].defaultSeasonId :
          null,
        userEmail: clientModel.user.emailAddress,
      },
      mediaSnowplowTrackerContext: clientModel.mediaSnowplowTrackerContext,
    };

    delete clientModel.feedback.feedbackEnabled;
    delete clientModel.roster.seasons;

    // The default selected season is the current one
    defaultState.roster.seasons.forEach(season => {
      season.isCurrentSeason = season.isSelected;
    });
    const { defaultState: defaultFeedState } = HudlFeed.store;

    // We use the jquery extend method in order to get a proper deep copy
    const initialState = $.extend(true, {}, defaultState, clientModel, defaultFeedState);

    const feedReducers = HudlFeed.reducers;
    const reducer = combineReducers(Object.assign({}, feedReducers, {
      routing: routerReducer,
    }, teamProfileApp));

    const createStoreWithMiddleware = applyMiddleware(routerMiddleware(browserHistory), thunkMiddleware)(createStore);

    const store = createStoreWithMiddleware(reducer, initialState);
    // Sync dispatched route actions to the history
    const isPublic = initialState.forcePublicView || !initialState.user || !initialState.user.hasAnyRoleOnTeam;
    const history = syncHistoryWithStore(browserHistory, store);
    let firstView = true;
    history.listen(location => {
      // Don't log through google analytics on first page view since kickoff does that by default
      usageLogger.logTeamLocation(location, initialState.team.teamId, isPublic, !firstView);
      firstView = false;

      if (!firstView) {
        promoteDynamicContent(clientModel.comScoreId);
      }
    });

    const rootElement = document.getElementById('app');

    const endOfTimelineCta = document.querySelector('#end-of-timeline > a');
    if (endOfTimelineCta) {
      endOfTimelineCta.addEventListener('click', () => {
        const newRoute = `/team/v2/${initialState.team.teamId}/${initialState.seoFriendlyName}/team`;
        store.dispatch(routerActions.push(newRoute));
      });
    }

    let teamAndSchedule;
    if (initialState.hideRosterAndSchedule) {
      teamAndSchedule = [
        <Redirect key="team-tab-route" from="team" to="/team/v2/:teamId/:seoFriendlyName" />,
        <Redirect key="schedule-tab-route" from="schedule" to="/team/v2/:teamId/:seoFriendlyName" />,
      ];
    } else {
      teamAndSchedule = [
        <Route key="team-tab-route" path="team" component={TeamTab} />,
        <Route key="schedule-tab-route" path="schedule" component={ScheduleTab} />,
      ];
    }

    const showRecruitGamesTab = clientModel.user.isOnTeamInRecruit || isRecruiter;

    render(
      <IntlProvider {...i18n}>
        <Provider store={store}>
          <Router history={history}>
            <Route path="/" component={WebNav}>
              <Route path="/team">
                <Route path="v2">
                  <Route path=":teamId">
                    <Route path=":seoFriendlyName" component={App}>
                      <IndexRoute component={TeamTimelineTab}/>
                      <Redirect from="timeline" to="/team/v2/:teamId/:seoFriendlyName" />
                      { !hasHighlightsDisabled && [
                        <Route path="highlights" component={TeamHighlightsTab}/>,
                        <Redirect from="videos" to="/team/v2/:teamId/:seoFriendlyName/highlights"/>,
                      ]}
                      { showRecruitGamesTab ?
                        <Route path="games" component={GamesTab}/> :
                        <Redirect from="games" to="/team/v2/:teamId/:seoFriendlyName"/>
                      }
                      {teamAndSchedule}
                    </Route>
                  </Route>
                </Route>
              </Route>
            </Route>
          </Router>
        </Provider>
      </IntlProvider>,
      rootElement,
      () => {
        if (!isMobile) {
          // Scroll part way down the header
          const scrollArea = document.querySelector('.parallax');
          if (scrollArea) {
            scrollArea.scrollTop = 270;
          }
        }
      });
    setupRateLimitedMouseFlow(data.getNullable('mouseFlowSourceUrl'), data.getNullable('mouseFlowRate') || 0,
      !data.getNullable('disableMouseFlowCookieCheck'));
  });
  loadAsyncScripts();
});
